import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../../store';
import { deleteMethod, get, post, put } from '../../../inteceptor';
import moment from 'moment';
import { updateCurrentLoggedInUser } from '../hrRedux/UserManagementRedux/userManagementRedux';

const initialState = {
  error: null,
  time: null,
  data: [],
  orderSummarydata: [],
  addData: {},
  addDataPOSItem: {},
  addCustomerData: {},
  drawerOpeningStatus: {},
  posDrawerData: {},
  posDrawerDataList: [],
  customer: [],
  returnCustomerSuccess: [],
  sorted: [],
  saleTypes: [],
  viewPosStatus: false,
  viewPosCloseStatus: false,
  sessionContext: null,
  sessionContextResolved: false,
  eligibleTransferRecipients: [],
  handoverSummary: null,
  requests: {
    sessionContext: { loading: false, error: null },
    eligibleTransferRecipients: { loading: false, error: null },
    createTransfer: { loading: false, error: null },
    reassignTransfer: { loading: false, error: null },
    cancelTransfer: { loading: false, error: null },
    acceptTransfer: { loading: false, error: null },
    handoverSummary: { loading: false, error: null },
  },
  transferDialogOpen: false,
  incomingTransferDialog: {
    open: false,
    dismissedTransferId: null,
  },
};

const STALE_SESSION_ERROR_CODES = new Set([
  'STALE_POS_SESSION',
  'ACTIVE_SESSION_NOT_FOUND',
  'TRANSFER_NOT_PENDING',
  'TRANSFER_NOT_FOUND',
  'TRANSFER_RECIPIENT_CHANGED',
  'TRANSFER_SOURCE_SESSION_CHANGED',
  'POS_SESSION_DRAWER_MISMATCH',
  'POS_SESSION_LOCATION_MISMATCH',
  'DRAWER_CLOSE_PENDING_TRANSFER',
]);

const getRequestError = (error) => ({
  code: error?.code || null,
  message: error?.message || 'An error occurred',
});

const slice = createSlice({
  name: 'POS',
  initialState,
  reducers: {
    // HAS ERROR
    hasError(state, action) {
      state.error = { message: action.payload, time: moment.now().toString() };
    },

    // Update Pos status
    updatePosStatus(state, value) {
      state.viewPosStatus = value.payload;
    },
    // Update Pos status
    updatePosCloseStatus(state, value) {
      state.viewPosCloseStatus = value.payload;
    },

    setSessionContext(state, action) {
      const sessionContext = action.payload || null;
      const incomingTransferId = sessionContext?.incomingPendingTransfer?.id || null;

      state.sessionContext = sessionContext;
      state.sessionContextResolved = true;
      // A normal context refresh intentionally makes a still-pending transfer visible again.
      state.incomingTransferDialog.open = Boolean(incomingTransferId);
      if (!incomingTransferId) state.incomingTransferDialog.dismissedTransferId = null;
    },
    setEligibleTransferRecipients(state, action) {
      state.eligibleTransferRecipients = Array.isArray(action.payload) ? action.payload : [];
    },
    setHandoverSummary(state, action) {
      state.handoverSummary = action.payload || null;
    },
    setRequestPending(state, action) {
      state.requests[action.payload] = { loading: true, error: null };
    },
    setRequestSuccess(state, action) {
      state.requests[action.payload] = { loading: false, error: null };
    },
    setRequestFailure(state, action) {
      state.requests[action.payload.request] = { loading: false, error: action.payload.error };
    },
    setTransferDialogOpen(state, action) {
      state.transferDialogOpen = action.payload;
    },
    dismissIncomingTransferDialog(state, action) {
      state.incomingTransferDialog.open = false;
      state.incomingTransferDialog.dismissedTransferId = action.payload;
    },
    setIncomingTransferDialogOpen(state, action) {
      state.incomingTransferDialog.open = action.payload;
    },

    // GET SaleManagement
    getCustomerSuccess(state, action) {
      state.customer = action.payload.data;
    },

    // GET SaleManagement
    getAllSaleTypesSuccess(state, action) {
      state.saleTypes = action.payload.data;
    },
    // GET SaleManagement
    getPosDrawerSuccess(state, action) {
      state.posDrawerDataList = action.payload.data;
    },
    // GET SaleManagement
    getDrawerOpeningStatus(state, action) {
      state.drawerOpeningStatus = action.payload.data;
    },
    // GET POS Drawer Data
    getPosDrawerData(state, action) {
      state.posDrawerData = action.payload.data;
    },
    // GET Pos Data
    getPosSuccess(state, action) {
      state.data = action.payload.data;
    },

    // GET Pos Order Summary Data
    getPosOrderSummarySuccess(state, action) {
      state.orderSummarydata = action.payload.data;
    },

    // Get POS Return Customers
    getPosReturnCustomerSuccess(state, action) {
      state.returnCustomerSuccess = action.payload.data;
    },

    addOrUpdatePos(state, action) {
      state.addData = {
        data: action.payload.data,
        time: moment.now().toString(),
      };
    },
    addOrUpdatePOSItemStatus(state, action) {
      state.addDataPOSItem = {
        data: action.payload.data,
        time: moment.now().toString(),
      };
    },

    clearAddOrUpdatePos(state) {
      state.addData = { data: null, time: moment.now().toString() };
    },

    addOrUpdateCustomer(state, action) {
      state.addCustomerData = {
        data: action.payload.data,
        time: moment.now().toString(),
      };
    },

    clearAll(state) {
      state.addData = {};
      state.error = {};
    },

    onBackStep(state) {
      state.checkout.activeStep -= 1;
    },

    onNextStep(state) {
      state.checkout.activeStep += 1;
    },

    onGotoStep(state, action) {
      const goToStep = action.payload;
      state.checkout.activeStep = goToStep;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { filterCategories } = slice.actions;

export const { setTransferDialogOpen, dismissIncomingTransferDialog, setIncomingTransferDialogOpen } = slice.actions;

export function updatePosDrawerSessionContext(sessionContext) {
  dispatch(slice.actions.setSessionContext(sessionContext));
}

async function recoverFromPosLifecycleError(error) {
  if (STALE_SESSION_ERROR_CODES.has(error?.code)) {
    await dispatch(updateUserDataAfterUpdate());
  }
}

function runTransferMutation({ request, makeRequest }) {
  return async () => {
    dispatch(slice.actions.setRequestPending(request));

    let response;
    try {
      response = await makeRequest();
    } catch (error) {
      dispatch(slice.actions.setRequestFailure({ request, error: getRequestError(error) }));

      try {
        await recoverFromPosLifecycleError(error);
      } catch (_refreshError) {
        // Keep the original mutation error as the authoritative result.
      }
      throw error;
    }

    dispatch(slice.actions.setSessionContext(response.data));
    dispatch(slice.actions.setRequestSuccess(request));

    try {
      await dispatch(updateUserDataAfterUpdate());
    } catch (_refreshError) {
      // The transfer already committed, so a secondary refresh must not mark it as failed.
    }

    return response;
  };
}

// ----------------------------------------------------------------------
export function viewPOSTab() {
  return async () => {
    dispatch(slice.actions.updatePosStatus(true));
  };
}
export function hidePOSTab() {
  return async () => {
    dispatch(slice.actions.updatePosStatus(false));
  };
}
// ----------------------------------------------------------------------
export function viewPOSCloseTab() {
  return async () => {
    dispatch(slice.actions.updatePosCloseStatus(true));
  };
}
export function hidePOSCloseTab() {
  return async () => {
    dispatch(slice.actions.updatePosCloseStatus(false));
  };
}

export function clearAddData() {
  return async () => {
    dispatch(slice.actions.clearAddOrUpdatePos());
  };
}

export function getPosDrawerList(body) {
  return async () => {
    try {
      const response = await get(`posDrawers`, body);
      if (response.status) {
        dispatch(slice.actions.getPosDrawerSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function openPOSDrawer(body) {
  return async () => {
    try {
      const response = await post(`openPOSDrawer`, body);
      if (response.status) {
        dispatch(slice.actions.getDrawerOpeningStatus(response));
        await dispatch(updateUserDataAfterUpdate());
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function closePOSDrawer(body) {
  return async () => {
    try {
      const response = await put(`closePOSDrawer`, body);
      if (response.status) {
        dispatch(slice.actions.getDrawerOpeningStatus(response));
        await dispatch(updateUserDataAfterUpdate());
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      await recoverFromPosLifecycleError(error);
    }
  };
}
export function getPOSDrawerDetails(id) {
  return async () => {
    try {
      const response = await get(`getDrawerPayments?id=${id}`);
      if (response.status) {
        dispatch(slice.actions.getPosDrawerData(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateUserDataAfterUpdate() {
  return async () => {
    try {
      const response = await post('auth', { token: localStorage.getItem('authTkn') });
      if (response.status) {
        localStorage.setItem('authenticatedUser', JSON.stringify(response?.userData));
        localStorage.setItem('permissionsList', JSON.stringify(response?.userData?.permissions));
        dispatch(slice.actions.setSessionContext(response?.userData?.posDrawerSessionContext || null));
        updateCurrentLoggedInUser(response?.userData);
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPosDrawerSessionContext() {
  return async () => {
    const request = 'sessionContext';
    dispatch(slice.actions.setRequestPending(request));
    try {
      const response = await get('posDrawerSessionContext');
      if (!response.status) throw response;
      dispatch(slice.actions.setSessionContext(response.data));
      dispatch(slice.actions.setRequestSuccess(request));
      return response;
    } catch (error) {
      dispatch(slice.actions.setRequestFailure({ request, error: getRequestError(error) }));
      throw error;
    }
  };
}

export function getPosDrawerTransferRecipients() {
  return async () => {
    const request = 'eligibleTransferRecipients';
    dispatch(slice.actions.setRequestPending(request));
    try {
      const response = await get('posDrawerTransferRecipients');
      if (!response.status) throw response;
      dispatch(slice.actions.setEligibleTransferRecipients(response.data));
      dispatch(slice.actions.setRequestSuccess(request));
      return response;
    } catch (error) {
      dispatch(slice.actions.setRequestFailure({ request, error: getRequestError(error) }));
      throw error;
    }
  };
}

export function getPosDrawerHandoverSummary(transferId) {
  return async () => {
    const request = 'handoverSummary';
    dispatch(slice.actions.setRequestPending(request));
    dispatch(slice.actions.setHandoverSummary(null));
    try {
      const query = transferId ? `?transferId=${encodeURIComponent(transferId)}` : '';
      const response = await get(`posDrawerHandoverSummary${query}`);
      if (!response.status) throw response;
      dispatch(slice.actions.setHandoverSummary(response.data));
      dispatch(slice.actions.setRequestSuccess(request));
      return response;
    } catch (error) {
      dispatch(slice.actions.setHandoverSummary(null));
      dispatch(slice.actions.setRequestFailure({ request, error: getRequestError(error) }));
      throw error;
    }
  };
}

export function createPosDrawerTransfer(toUserId) {
  return runTransferMutation({
    request: 'createTransfer',
    makeRequest: () => post('posDrawerTransfers', { toUserId }),
  });
}

export function reassignPosDrawerTransfer(transferId, toUserId) {
  return runTransferMutation({
    request: 'reassignTransfer',
    makeRequest: () => put(`posDrawerTransfers/${transferId}/reassign`, { toUserId }),
  });
}

export function cancelPosDrawerTransfer(transferId) {
  return runTransferMutation({
    request: 'cancelTransfer',
    makeRequest: () => deleteMethod(`posDrawerTransfers/${transferId}`),
  });
}

export function acceptPosDrawerTransfer(transferId) {
  return runTransferMutation({
    request: 'acceptTransfer',
    makeRequest: () => post(`posDrawerTransfers/${transferId}/accept`, {}),
  });
}

export function getAllSaleTypes() {
  return async () => {
    try {
      const response = await get(`allSaleTypes`);
      if (response.status) {
        dispatch(slice.actions.getAllSaleTypesSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getPos(locationId, max = -1) {
  return async () => {
    try {
      const response = await get(`pos?locationId=${locationId}&maxNumber=${max}`);
      if (response.status) {
        dispatch(slice.actions.getPosSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
// ----------------------------------------------------------------------

export function getPosReturnCustomerReport(locationId, fromDate, toDate) {
  return async () => {
    try {
      const response = await get(`posReturnCustomerReport?locationId=${locationId}&fromDate=${fromDate}}&toDate=${toDate}`);
      if (response.status) {
        dispatch(slice.actions.getPosReturnCustomerSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getForKitchenDisplay(locationId) {
  return async () => {
    try {
      const response = await get(`getForKitchenDisplay?locationId=${locationId}`);
      if (response.status) {
        dispatch(slice.actions.getPosSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function addPosSale(body) {
  return async () => {
    try {
      const response = await post(`pos`, body);
      if (response.status) {
        dispatch(slice.actions.addOrUpdatePos(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      await recoverFromPosLifecycleError(error);
    }
  };
}

// ----------------------------------------------------------------------

export function markPOSAsSold(body) {
  return async () => {
    try {
      const response = await put(`markPOSAsSold`, body);
      if (response.status) {
        dispatch(slice.actions.addOrUpdatePos(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function updatePOSItemStatus(body) {
  return async () => {
    try {
      const response = await put(`updatePOSItemStatus`, body);
      if (response.status) {
        dispatch(slice.actions.addOrUpdatePOSItemStatus(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
// ----------------------------------------------------------------------

export function updatePosStatus(body) {
  return async () => {
    try {
      const response = await put(`updatePosStatus`, body);
      if (response.status) {
        dispatch(slice.actions.addOrUpdatePos(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function updatePosPayments(body) {
  return async () => {
    try {
      const response = await put(`posPayments`, body);
      if (response.status) {
        dispatch(slice.actions.addOrUpdatePos(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function deleteSale(body) {
  return async () => {
    try {
      const response = await deleteMethod(`sale`, body);
      if (response.status) {
        dispatch(slice.actions.addOrUpdatePos(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getPosOrderSummary(locationId, max = -1) {
  return async () => {
    try {
      const response = await get(`posOrderSummary?locationId=${locationId}&maxNumber=${max}`);
      if (response.status) {
        dispatch(slice.actions.getPosOrderSummarySuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
